import { useState, useCallback, useMemo, useRef, useEffect, useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';
import ChatPanel from './components/ChatPanel';
import ModelCatalog from './components/ModelCatalog';
import ParameterControls from './components/ParameterControls';
import ModelDetailsModal from './components/ModelDetailsModal';
import AppScreenWrapper from '../../../components/app-shell/AppScreenWrapper';
import Sidebar from '../../../components/app-shell/Sidebar';
import ScreenOrbs from '../../../components/app-shell/ScreenOrbs';
import useClientData from '../../../hooks/useClientData';
import authSlice from '../../../utils/redux/slice/authSlice';
import { PlusIcon, CaretIcon, CloseIcon, TrashIcon } from '../../../components/app-shell/icons';
import {
  subscribe as subscribeThreads,
  getThreadIndex,
  deleteThread,
  createHistoryAdapter,
} from './playgroundThreadStore';
import './llmPlayground.css';

const DEFAULT_PARAMS = { temperature: 0.7, maxTokens: 4096, systemPrompt: '' };

const timeAgo = (iso) => {
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return 'Now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return `${Math.floor(s / 604800)}w`;
};

export default function LlmPlaygroundScreen() {
  const { balance, clientName, clientEmail } = useClientData();
  const user = useSelector(authSlice.selectors.getUser);
  const clientId = user?.client_id || user?.client_email || 'anon';
  const [selectedModel, setSelectedModel] = useState('gpt-4o-mini');
  const [activeThreadId, setActiveThreadId] = useState(() => crypto.randomUUID());
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [detailsModel, setDetailsModel] = useState(null);
  const [showParams, setShowParams] = useState(false);
  const [contextOpen, setContextOpen] = useState(true);
  const [modelCatalogOpen, setModelCatalogOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const threads = useSyncExternalStore(subscribeThreads, () => getThreadIndex(clientId));

  // Meta read per-append via ref so the adapter stays stable per thread
  const metaRef = useRef({});
  metaRef.current = { model: selectedModel, params };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const historyAdapter = useMemo(
    () => createHistoryAdapter(clientId, activeThreadId, () => metaRef.current),
    [clientId, activeThreadId]
  );

  const handleSelectModel = useCallback(name => setSelectedModel(name), []);

  const newConversation = useCallback(() => {
    setActiveThreadId(crypto.randomUUID());
  }, []);

  const openThread = useCallback((t) => {
    setActiveThreadId(t.id);
    if (t.model) setSelectedModel(t.model);
    if (t.params) setParams(prev => ({ ...prev, ...t.params }));
  }, []);

  const handleDeleteThread = useCallback((t) => {
    deleteThread(clientId, t.id);
    setDeleteConfirmId(null);
    if (t.id === activeThreadId) setActiveThreadId(crypto.randomUUID());
  }, [clientId, activeThreadId]);

  // Inline delete-confirm auto-dismisses after ~3s
  useEffect(() => {
    if (!deleteConfirmId) return undefined;
    const timer = setTimeout(() => setDeleteConfirmId(null), 3000);
    return () => clearTimeout(timer);
  }, [deleteConfirmId]);

  return (
    <AppScreenWrapper>
      <div className="screen-root" style={{ display: 'flex' }}>
        <Sidebar balance={balance} name={clientName} email={clientEmail} />

        {/* Thread panel — left (exact from design) */}
        <aside className="app-side-panel" style={{
          width: 260, flex: '0 0 260px', height: '100%',
          borderRight: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column',
          background: 'rgba(255,255,255,.015)',
        }}>
          <div style={{ padding: 16 }}>
            <button className="btn btn-primary btn-sm" style={{ width: '100%', justifyContent: 'center', height: 38 }} onClick={newConversation}>
              <PlusIcon s={14} /> New conversation
            </button>
          </div>

          <div className="scroll-y" style={{ flex: 1, padding: '0 8px 12px' }}>
            {threads.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-4)', lineHeight: 1.5, padding: '8px 8px' }}>
                No saved conversations yet. Start chatting and they'll appear here.
              </div>
            ) : (
              threads.map(t => {
                const active = t.id === activeThreadId;
                return (
                  <div
                    key={t.id}
                    className="pg-thread-row"
                    onClick={() => openThread(t)}
                    style={{
                      display: 'block', padding: '10px 12px', borderRadius: 10, marginBottom: 4,
                      background: active ? 'rgba(255,255,255,.05)' : 'transparent',
                      borderLeft: active ? '2px solid #FF4D8D' : '2px solid transparent',
                      cursor: 'pointer', position: 'relative',
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: active ? 600 : 500, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: 24 }}>
                      {t.title || 'New conversation'}
                    </div>
                    <div className="row" style={{ justifyContent: 'space-between', marginTop: 4 }}>
                      {t.model
                        ? <span className="chip" style={{ height: 18, fontSize: 10, color: '#A5B4FC', borderColor: 'rgba(129,140,248,.3)', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.model}</span>
                        : <span />}
                      <span style={{ fontSize: 10, color: 'var(--text-4)' }}>{timeAgo(t.updated_at)}</span>
                    </div>
                    <span style={{ position: 'absolute', top: 8, right: 8 }} onClick={e => e.stopPropagation()}>
                      {deleteConfirmId === t.id ? (
                        <button
                          onClick={() => handleDeleteThread(t)}
                          style={{ fontSize: 10, color: 'var(--red)', background: 'rgba(245,87,108,.12)', border: '1px solid rgba(245,87,108,.4)', borderRadius: 6, cursor: 'pointer', fontWeight: 600, padding: '2px 6px' }}
                        >
                          Delete?
                        </button>
                      ) : (
                        <button
                          className="pg-thread-del"
                          title="Delete conversation"
                          onClick={() => setDeleteConfirmId(t.id)}
                          style={{ background: 'transparent', border: 'none', color: 'var(--text-4)', cursor: 'pointer', padding: 2, opacity: 0, transition: 'opacity .15s' }}
                        >
                          <TrashIcon s={12} />
                        </button>
                      )}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </aside>

        {/* Center — Conversation */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}>
          <ScreenOrbs tone="indigo" />

          {/* Model + params bar */}
          <div className="row" style={{ height: 56, flex: '0 0 56px', padding: '0 22px', borderBottom: '1px solid var(--border)', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
            <div className="row" style={{ gap: 14 }}>
              <button className="row" style={{
                gap: 8, height: 34, padding: '0 12px', borderRadius: 10,
                background: 'rgba(255,255,255,.04)', border: '1px solid var(--border)', cursor: 'pointer',
              }} onClick={() => setModelCatalogOpen(v => !v)}>
                <span style={{ width: 14, height: 14, borderRadius: 4, background: 'linear-gradient(135deg, #10B981, #6366F1)' }} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>{selectedModel}</span>
                <CaretIcon s={10} />
              </button>
              <div className="row" style={{ gap: 8, fontSize: 12, color: 'var(--text-3)' }}>
                <span>Temp</span>
                <div style={{ width: 120, height: 4, background: 'rgba(255,255,255,.08)', borderRadius: 2, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ width: `${(params.temperature / 2) * 100}%`, height: '100%', background: 'var(--grad)', borderRadius: 2 }} />
                </div>
                <span className="mono" style={{ color: '#fff' }}>{params.temperature}</span>
              </div>
            </div>
            <div className="row" style={{ gap: 8 }}>
              <button className="btn btn-ghost btn-sm app-mobile-only" onClick={newConversation}>
                <PlusIcon s={12} /> New
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowParams(v => !v)}>System prompt</button>
              <button className="btn btn-ghost btn-sm app-side-panel" style={{ color: 'var(--text-3)' }} onClick={() => setContextOpen(v => !v)}>
                {contextOpen ? 'Hide context' : 'Context'}
              </button>
            </div>
          </div>

          {/* Params panel */}
          {showParams && (
            <div style={{ borderBottom: '1px solid var(--border)', background: 'rgba(255,255,255,.02)', position: 'relative', zIndex: 1 }}>
              <ParameterControls params={params} onChange={setParams} />
            </div>
          )}

          {/* Model catalog dropdown */}
          {modelCatalogOpen && (
            <div style={{ position: 'absolute', top: 56, left: 22, width: 320, maxHeight: 400, overflow: 'auto', background: 'rgba(10,10,18,.97)', border: '1px solid var(--border)', borderRadius: 14, zIndex: 100, boxShadow: '0 20px 60px rgba(0,0,0,.6)', backdropFilter: 'blur(20px)' }}>
              <ModelCatalog selectedModel={selectedModel} onSelectModel={m => { handleSelectModel(m); setModelCatalogOpen(false); }} onShowDetails={setDetailsModel} />
            </div>
          )}

          {/* Chat area */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative', zIndex: 1 }}>
            <div className="aui-root" style={{ height: '100%', width: '100%' }}>
              {/* Keyed by thread only — switching models mid-thread keeps the
                  conversation (comparing models on one context is the point) */}
              {/* clientId is in the key too: the history adapter loads once per
                  mount, so a clientId change without remount would load from one
                  namespace and append into another */}
              <ChatPanel key={`${clientId}:${activeThreadId}`} selectedModel={selectedModel} params={params} historyAdapter={historyAdapter} />
            </div>
          </div>
        </div>

        {/* Right context panel — exact from design */}
        {contextOpen && (
          <aside className="app-side-panel" style={{
            width: 280, flex: '0 0 280px', height: '100%',
            borderLeft: '1px solid var(--border)', padding: 18,
            display: 'flex', flexDirection: 'column', gap: 14,
            background: 'rgba(255,255,255,.015)',
          }}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)', letterSpacing: '.04em', textTransform: 'uppercase' }}>Context</div>
              <button style={{ background: 'transparent', border: 'none', color: 'var(--text-4)', cursor: 'pointer' }} onClick={() => setContextOpen(false)}><CloseIcon s={14} /></button>
            </div>

            {/* Model info card */}
            <div className="card" style={{ padding: 14 }}>
              <div className="row" style={{ gap: 10 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg, #10B981, #6366F1)', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 700 }}>AI</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{selectedModel}</div>
              </div>
            </div>

          </aside>
        )}
      </div>

      {detailsModel && <ModelDetailsModal model={detailsModel} onClose={() => setDetailsModel(null)} />}
    </AppScreenWrapper>
  );
}
